/**
 * IndexedDB persistence for UI preferences.
 *
 * Replaces localStorage for hook-based preferences to avoid the ~5 MB shared quota.
 */

import { USER_COLLAPSE, USER_EXPAND } from 'common/actions/row-groups/types';
import { CAP_TABLE_ROW_GROUPS_PREFERENCES_KEY } from 'common/constants/cap-table';
import {
  PINNED_COMPANIES_KEY,
  PINNED_FIRMS_KEY,
  PINNED_FUND_INVESTMENTS_KEY,
  PINNED_FUNDS_KEY,
  preferenceKeyForUser,
  SIDEBAR_ACTIVE_SECTION_KEY,
} from 'common/constants/userPreferences';

export const PREFERENCES_DB_NAME = 'scalar:preferences';
const DB_VERSION = 1;
const STORE_NAME = 'entries';

export const PREFERENCES_BROADCAST_CHANNEL = 'scalar:preferences';

interface PreferenceRecord {
  key: string;
  value: unknown;
  updatedAt: number;
}

export interface PreferenceChangeMessage {
  key: string;
  value: unknown;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) {
    return dbPromise;
  }
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(PREFERENCES_DB_NAME, DB_VERSION);
    req.onupgradeneeded = e => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    req.onsuccess = e => resolve((e.target as IDBOpenDBRequest).result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });
  return dbPromise;
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function isQuotaError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const domError = error as DOMException;
  return (
    domError.name === 'QuotaExceededError' ||
    domError.code === 22 ||
    domError.code === 1014
  );
}

export async function getPreference<T>(key: string): Promise<T | undefined> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const record = await idbRequest<PreferenceRecord | undefined>(
      tx.objectStore(STORE_NAME).get(key)
    );
    return record?.value as T | undefined;
  } catch {
    return undefined;
  }
}

export async function setPreference(key: string, value: unknown): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  await idbRequest(
    tx.objectStore(STORE_NAME).put({
      key,
      value,
      updatedAt: Date.now(),
    } satisfies PreferenceRecord)
  );
  broadcastPreferenceChange(key, value);
}

export async function removePreference(key: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await idbRequest(tx.objectStore(STORE_NAME).delete(key));
  } catch {
    // no-op
  }
}

export function broadcastPreferenceChange(key: string, value: unknown): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent('localStorageChange', {
      detail: { key, value },
    })
  );
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(PREFERENCES_BROADCAST_CHANNEL);
      channel.postMessage({ key, value } satisfies PreferenceChangeMessage);
      channel.close();
    }
  } catch {
    // no-op
  }
}

/**
 * One-time migration from a legacy localStorage key into IndexedDB.
 * @cleanup Remove this function (and all call sites) ~8 weeks after the IndexedDB migration ships.
 *          Call sites: useLocalStorage.ts, userPreferences.ts, useColorPalette.ts, useJobsColorPalette.ts.
 */
export async function migrateLegacyLocalStorageKey(key: string): Promise<unknown | undefined> {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const existing = await getPreference(key);
    if (existing !== undefined) {
      return existing;
    }

    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return undefined;
    }

    const parsed: unknown = JSON.parse(raw);
    await setPreference(key, parsed);
    window.localStorage.removeItem(key);
    return parsed;
  } catch {
    return undefined;
  }
}

const isRowPreferenceValue = (value: unknown): boolean =>
  value === USER_EXPAND || value === USER_COLLAPSE;

/** Legacy cap-table blob stored row aliases at the root (no user-id wrapper). */
const isFlatCapTableRowGroupPrefs = (prefs: Record<string, unknown>): boolean =>
  Object.values(prefs).some(isRowPreferenceValue);

/** Legacy blob keyed by cap table id -> row prefs (no user-id wrapper). */
const isNestedCapTableRowGroupPrefs = (prefs: Record<string, unknown>): boolean => {
  const values = Object.values(prefs);
  if (values.length === 0) {
    return false;
  }
  return values.every(
    value =>
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      isFlatCapTableRowGroupPrefs(value as Record<string, unknown>)
  );
};

const isNumericKey = (key: string): boolean => /^\d+$/.test(key);

const migrateLegacyBlobToPerUser = async (
  legacyKey: string,
  perUserKey: string,
  value: unknown
): Promise<unknown> => {
  await setPreference(perUserKey, value);
  window.localStorage.removeItem(legacyKey);
  return value;
};

/**
 * Extract one user's slice from a legacy multi-user localStorage blob and persist under a per-user key.
 * @cleanup Remove this function (and all call sites) ~8 weeks after the IndexedDB migration ships.
 *          Call sites: useLocalStorageByUser.ts, userPreferences.ts, runUserPreferenceMigrations.
 */
export async function migrateLegacyMultiUserPreference(
  legacyKey: string,
  perUserKey: string,
  userId: number
): Promise<unknown | undefined> {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const existing = await getPreference(perUserKey);
  if (existing !== undefined) {
    return existing;
  }

  try {
    const raw = window.localStorage.getItem(legacyKey);
    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') {
      return undefined;
    }

    const userKey = String(userId);
    if (userKey in parsed || userId in parsed) {
      const userValue = parsed[userKey] ?? (parsed as Record<number, unknown>)[userId];
      await setPreference(perUserKey, userValue);
      delete parsed[userKey];
      delete (parsed as Record<number, unknown>)[userId];
      const remainingIds = Object.keys(parsed);
      if (remainingIds.length === 0) {
        window.localStorage.removeItem(legacyKey);
      } else {
        window.localStorage.setItem(legacyKey, JSON.stringify(parsed));
      }
      return userValue;
    }

    // Cap-table prefs were sometimes stored as a flat row map or per-cap-table map at the legacy root key.
    if (legacyKey === CAP_TABLE_ROW_GROUPS_PREFERENCES_KEY) {
      if (isFlatCapTableRowGroupPrefs(parsed) || isNestedCapTableRowGroupPrefs(parsed)) {
        return migrateLegacyBlobToPerUser(legacyKey, perUserKey, parsed);
      }
    }

    // Other users' slices still in the blob — do not claim the whole key for the current user.
    const hasOtherUserSlices = Object.keys(parsed).some(
      key => isNumericKey(key) && key !== userKey
    );
    if (!hasOtherUserSlices && Object.keys(parsed).length > 0) {
      return migrateLegacyBlobToPerUser(legacyKey, perUserKey, parsed);
    }
  } catch {
    // best-effort
  }

  return migrateLegacyLocalStorageKey(perUserKey);
}

/**
 * Run legacy localStorage migrations for all per-user preference keys once per session.
 * Cap-table prefs only migrated when visiting the tab; this ensures they move on any main-app load.
 */
export async function runUserPreferenceMigrations(userId: number): Promise<void> {
  const keys = [
    PINNED_COMPANIES_KEY,
    PINNED_FUND_INVESTMENTS_KEY,
    PINNED_FUNDS_KEY,
    PINNED_FIRMS_KEY,
    SIDEBAR_ACTIVE_SECTION_KEY,
    CAP_TABLE_ROW_GROUPS_PREFERENCES_KEY,
  ];

  await Promise.all(
    keys.map(baseKey =>
      migrateLegacyMultiUserPreference(baseKey, preferenceKeyForUser(baseKey, userId), userId)
    )
  );
}

export function subscribePreferenceChanges(
  onChange: (message: PreferenceChangeMessage) => void
): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleCustom = (e: Event) => {
    const {detail} = (e as CustomEvent<{ key: string; value: unknown }>);
    if (detail?.key) {
      onChange({ key: detail.key, value: detail.value });
    }
  };

  window.addEventListener('localStorageChange', handleCustom as EventListener);

  let channel: BroadcastChannel | null = null;
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(PREFERENCES_BROADCAST_CHANNEL);
    channel.onmessage = (e: MessageEvent<PreferenceChangeMessage>) => {
      if (e.data?.key) {
        onChange(e.data);
      }
    };
  }

  return () => {
    window.removeEventListener('localStorageChange', handleCustom as EventListener);
    channel?.close();
  };
}
